import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlphaLoader } from "@/components/ui/alpha-loader";

interface Article {
  id: string;
  title: string;
  content: string;
  topic_id: number;
  topic_name: string;
  author: string | null;
  reading_time_minutes: number | null;
  is_published: boolean;
  created_at: string;
  updated_at: string;
}

interface ArticleEditorProps {
  article?: Article;
  onSave: () => void;
}

interface Topic {
  id: number;
  name: string;
  description: string | null;
  icon_name: string | null;
  color_class: string | null;
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const calculateReadingTime = (content: string): number => {
  const wordsPerMinute = 200;
  const wordCount = content.trim().split(/\s+/).length;
  return Math.max(1, Math.ceil(wordCount / wordsPerMinute));
};

const ArticleEditor = ({ article, onSave }: ArticleEditorProps) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    topic_id: 1,
    author: '',
    is_published: false,
  });
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingTopics, setLoadingTopics] = useState(true);
  const [previewMode, setPreviewMode] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const { toast } = useToast();

  const fetchTopics = async () => {
    try {
      const { data, error } = await supabase
        .from('kinkipedia_topics')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setTopics(data || []);
    } catch (error) {
      console.error('Error fetching topics:', error);
      toast({
        title: "Error",
        description: "Failed to fetch topics",
        variant: "destructive",
      });
    } finally {
      setLoadingTopics(false);
    }
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  useEffect(() => {
    if (article) {
      setFormData({
        title: article.title,
        content: article.content,
        topic_id: article.topic_id,
        author: article.author || '',
        is_published: article.is_published,
      });
    }
    setShowSuccess(false); // Reset success state when article changes
  }, [article]);

  const handleSave = async () => {
    console.log('handleSave called, current formData:', formData);
    
    if (!formData.title.trim() || !formData.content.trim()) {
      toast({
        title: "Error",
        description: "Title and content are required",
        variant: "destructive",
      });
      return;
    }

    console.log('Starting save process...');
    setLoading(true);
    try {
      const selectedTopic = topics.find(t => t.id === formData.topic_id);
      const readingTime = calculateReadingTime(formData.content);
      
      const articleData = {
        title: formData.title.trim(),
        content: formData.content.trim(),
        topic_id: formData.topic_id,
        topic_name: selectedTopic?.name || '',
        author: formData.author.trim() || null,
        reading_time_minutes: readingTime,
        is_published: formData.is_published,
        updated_at: new Date().toISOString(),
      };

      console.log('Saving article data:', articleData);

      if (article && article.id) {
        console.log('Updating existing article:', article.id);
        // Update existing article
        const { error } = await supabase
          .from('kinkipedia_articles')
          .update(articleData)
          .eq('id', article.id);

        if (error) {
          console.error('Update error:', error);
          throw error;
        }

        console.log('Article updated successfully');
        toast({
          title: "Success",
          description: "Article updated successfully",
        });
      } else {
        console.log('Creating new article');
        // Create new article
        const { error } = await supabase
          .from('kinkipedia_articles')
          .insert([articleData]);

        if (error) {
          console.error('Insert error:', error);
          throw error;
        }

        console.log('Article created successfully');
        toast({
          title: "Success",
          description: "Article created successfully",
        });
      }

      // Show success state and delay dialog closure
      console.log('Setting success state...');
      setShowSuccess(true);
      setTimeout(() => {
        console.log('Calling onSave after delay...');
        onSave();
      }, 2000);
    } catch (error) {
      console.error('Error saving article:', error);
      toast({
        title: "Error",
        description: "Failed to save article",
        variant: "destructive",
      });
      // Don't call onSave() on error to preserve form data
    } finally {
      setLoading(false);
    }
  };

  const estimatedReadingTime = calculateReadingTime(formData.content);

  if (loadingTopics) {
    return (
      <div className="flex justify-center items-center h-32">
        <AlphaLoader size="mini" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Tabs defaultValue="edit" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="edit">Edit</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>
        
        <TabsContent value="edit" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="title" className="text-ice-white">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Article title"
                className="bg-carbon border-ice-white/20 text-ice-white"
              />
            </div>

            <div>
              <Label htmlFor="author" className="text-ice-white">Author</Label>
              <Input
                id="author"
                value={formData.author}
                onChange={(e) => setFormData({ ...formData, author: e.target.value })}
                placeholder="Author name (optional)"
                className="bg-carbon border-ice-white/20 text-ice-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="topic" className="text-ice-white">Topic *</Label>
              <Select
                value={formData.topic_id.toString()}
                onValueChange={(value) => setFormData({ ...formData, topic_id: parseInt(value) })}
              >
                <SelectTrigger className="bg-carbon border-ice-white/20 text-ice-white">
                  <SelectValue placeholder="Select topic" />
                </SelectTrigger>
                <SelectContent className="bg-carbon border-ice-white/20">
                  {topics.map((topic) => (
                    <SelectItem key={topic.id} value={topic.id.toString()}>
                      {topic.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="published"
                  checked={formData.is_published}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_published: checked })}
                />
                <Label htmlFor="published" className="text-ice-white">Published</Label>
              </div>
              
              <div className="text-sm text-ice-white/60">
                Est. reading time: {estimatedReadingTime} min
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="content" className="text-ice-white">Content *</Label>
            <Textarea
              id="content"
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Article content in markdown format..."
              className="bg-carbon border-ice-white/20 text-ice-white font-mono text-sm"
              rows={30}
            />
            <div className="text-xs text-ice-white/50 mt-1">
              Supports markdown formatting. Use **bold**, *italic*, # headers, - lists, etc.
            </div>
          </div>
        </TabsContent>

        <TabsContent value="preview">
          <Card>
            <CardHeader>
              <CardTitle className="text-ice-white">{formData.title || "Untitled Article"}</CardTitle>
              <CardDescription>
                {topics.find(t => t.id === formData.topic_id)?.name || 'Unknown Topic'} • 
                By {formData.author || "Unknown"} • 
                {estimatedReadingTime} min read
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="prose prose-invert max-w-none">
                <pre className="whitespace-pre-wrap text-ice-white text-sm leading-relaxed">
                  {formData.content || "No content yet..."}
                </pre>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-2 pt-4 border-t border-ice-white/20">
        {showSuccess && (
          <div className="flex items-center text-toxic-green text-sm mr-4">
            ✓ {article ? "Article updated successfully! Dialog will close shortly..." : "Article created successfully! Dialog will close shortly..."}
          </div>
        )}
        <Button
          onClick={handleSave}
          disabled={loading || showSuccess}
          className="bg-neon-red hover:bg-neon-red/80 text-white"
        >
          {showSuccess ? "Success!" : loading ? "Saving..." : (article ? "Update Article" : "Create Article")}
        </Button>
      </div>
    </div>
  );
};

export default ArticleEditor;